import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import moment from 'moment';
import { createContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom';
import courseService from 'services/course';
import { INSTITUTIONS, MAJORS, SEMESTER_OPTIONS } from 'utils/constants';
import { v4 as uuid } from 'uuid';

const defaultForm = {
    courseName: '',
    institutionName: null,
    otherInstitutionName: '',
    fieldOfStudy: null,
    faculty: '',
    semester: null,
    section: '',
};

const LIST_OF_SCHOOLS = [...INSTITUTIONS, { name: 'Other' }];

const PageContext = createContext();

const PageProvider = (props) => {
    const history = useHistory();
    const location = useLocation();
    const { auth, changeAccount, confirmCourseSetUp } = useAuth();
    const { course } = useApp();

    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({ ...defaultForm, course_id: uuid() });
    const [isDeletable, setIsDeletable] = useState(false);
    const [dialog, setDialog] = useState({
        open: false,
        message: '',
        description: '',
        variant: 'primary',
        actionButtonText: '',
        onAction: () => {},
    });
    const [errors, setErrors] = useState({
        courseName: '',
        faculty: '',
        institutionName: '',
        otherInstitutionName: '',
        fieldOfStudy: '',
        semester: '',
    });

    const handleChange = async (key, data) => {
        switch (key) {
            case 'courseName': {
                return setForm((prev) => ({ ...prev, courseName: data }));
            }

            case 'institutionName': {
                return setForm((prev) => ({ ...prev, institutionName: data }));
            }

            case 'otherInstitutionName': {
                return setForm((prev) => ({ ...prev, otherInstitutionName: data }));
            }

            case 'fieldOfStudy': {
                return setForm((prev) => ({ ...prev, fieldOfStudy: data }));
            }

            case 'faculty': {
                return setForm((prev) => ({ ...prev, faculty: data }));
            }

            case 'section': {
                return setForm((prev) => ({ ...prev, section: data }));
            }

            case 'semester': {
                return setForm((prev) => ({ ...prev, semester: data }));
            }

            default:
                return;
        }
    };

    const getErrorMessage = (key, data, form) => {
        switch (key) {
            case 'courseName': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Course name cannot be left blank';
            }

            case 'faculty': {
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Department cannot be left blank';
            }

            case 'institutionName': {
                const value = `${data?.value || ''}`.trim();
                return value && value?.length > 0 ? '' : 'You must make a selection';
            }

            case 'otherInstitutionName': {
                if (form?.institutionName?.value !== 'Other') return '';
                const value = `${data || ''}`.trim();
                return value && value?.length > 0 ? '' : 'Institution name cannot be left blank';
            }

            case 'semester': {
                const value = `${data?.value || ''}`.trim();
                return value && value?.length > 0 ? '' : 'You must make a selection';
            }

            case 'fieldOfStudy': {
                const value = `${data?.value || ''}`.trim();
                return value && value?.length > 0 ? '' : 'You must make a selection';
            }

            default:
                return '';
        }
    };

    const validate = (key, value) => {
        const errorMessage = getErrorMessage(key, value, form);
        setErrors((errors) => {
            return {
                ...errors,
                [key]: errorMessage,
            };
        });
    };

    const validateAll = () => {
        return !Boolean(
            Object.keys(errors)
                .map((key) => {
                    validate(key, form[key]);
                    return getErrorMessage(key, form[key], form);
                })
                .reduce((cur, acc) => {
                    return cur + acc;
                }, '')
        );
    };

    const deleteCourse = async (courseId) => {
        setLoading(true);
        await courseService.deleteCourse(courseId, { course_id: courseId });
        setLoading(false);
    };

    const openDeleteDialog = () => {
        setDialog((current) => ({
            ...current,
            open: true,
            message: `Delete ${form.name || 'this course'}?`,
            description:
                'This course will be deleted permanently from our system, and users will lose access immediately. Please note that this action cannot be undone.',
            variant: 'destructive',
            actionButtonText: 'Delete',
            onAction: () => {
                setDialog((current) => ({ ...current, open: false, onAction: () => {} }));
                deleteCourse(form.course_id);
                changeAccount();
            },
        }));
    };

    const handleClose = async () => {
        setLoading(true);
        const data = {
            course_id: form.course_id,
            name: form.courseName,
            section: form.section,
            faculty: form.faculty,
            school:
                form.institutionName?.value === 'Other' && form.otherInstitutionName?.length > 0
                    ? form.otherInstitutionName
                    : form.institutionName?.value,
            semester: form.semester?.value,
            field_of_study: form.fieldOfStudy?.value,
            root_url: window.location.origin,
            finished_course_set_up: false,
        };

        if (data.name || data.section || data.faculty || data.school || data.semester || data.field_of_study) {
            await confirmCourseSetUp(data);
        }

        setForm({ ...defaultForm, course_id: uuid() });
        setIsDeletable(false);
        setLoading(false);
        return changeAccount();
    };

    const submitData = async () => {
        if (!validateAll()) return;
        setLoading(true);
        const data = {
            course_id: form.course_id,
            name: form.courseName,
            section: form.section,
            faculty: form.faculty,
            school:
                form.institutionName?.value === 'Other' && form.otherInstitutionName?.length > 0
                    ? form.otherInstitutionName
                    : form.institutionName?.value,
            semester: form.semester?.value,
            field_of_study: form.fieldOfStudy?.value,
            start_on: form.start_on?.valueOf(),
            end_on: form.end_on?.valueOf(),
            root_url: window.location.origin,
            finished_course_set_up: true,
        };
        if (form.original_course_id) {
            data.original_course_id = form.original_course_id;
        }
        await confirmCourseSetUp(data);
        setForm({ ...defaultForm, course_id: uuid() });
        setIsDeletable(false);
        setLoading(false);
        return history.push('/');
    };

    useEffect(() => {
        if (auth.course_id && course) {
            const requestCourseSetUpDraft = async () => {
                setLoading(true);
                const existingCourseData = course;
                if (existingCourseData?.finished_course_set_up) return history.push('/');
                const selectedSchool = LIST_OF_SCHOOLS?.find(({ name }) => name === existingCourseData?.school);
                const selectedMajor = MAJORS?.find(({ name }) => name === existingCourseData?.field_of_study);
                const selectedSemester = SEMESTER_OPTIONS?.find(({ value }) => value === existingCourseData?.semester);
                const startOnDate = existingCourseData?.start_on;
                const endOnDate = existingCourseData?.end_on;
                const incomingCourseData = {
                    course_id: existingCourseData?.id,
                    courseName: existingCourseData?.name || '',
                    faculty: existingCourseData?.faculty || '',
                    section: existingCourseData?.section || '',
                    projectName: existingCourseData?.project_title || '',
                    start_on: startOnDate ? moment(startOnDate) : null,
                    end_on: endOnDate ? moment(endOnDate) : null,

                    institutionName: selectedSchool ? { key: selectedSchool.name, value: selectedSchool.name } : null,
                    fieldOfStudy: selectedMajor ? { key: selectedMajor.name, value: selectedMajor.name } : null,
                    semester: selectedSemester ? { key: selectedSemester.key, value: selectedSemester.value } : null,

                    is_lti: process.env.REACT_APP_AUTH === 'LTI',
                };
                setForm((prev) => ({ ...prev, ...incomingCourseData }));
                setIsDeletable(true);
                setLoading(false);
            };
            requestCourseSetUpDraft();
        } else if (location?.state?.duplicatingCourse) {
            const duplicatingCourse = location.state.duplicatingCourse;
            const selectedSchool = LIST_OF_SCHOOLS?.find(({ name }) => name === duplicatingCourse?.school);
            const selectedMajor = MAJORS?.find(({ name }) => name === duplicatingCourse?.field_of_study);
            const selectedSemester = SEMESTER_OPTIONS?.find(({ value }) => value === duplicatingCourse?.semester);
            const incomingCourseData = {
                course_id: duplicatingCourse?.id,
                courseName: duplicatingCourse?.name || '',
                faculty: duplicatingCourse?.faculty || '',
                section: duplicatingCourse?.section || '',
                institutionName: selectedSchool ? { key: selectedSchool.name, value: selectedSchool.name } : null,
                fieldOfStudy: selectedMajor ? { key: selectedMajor.name, value: selectedMajor.name } : null,
                semester: selectedSemester ? { key: selectedSemester.key, value: selectedSemester.value } : null,
                original_course_id: duplicatingCourse.original_course_id,
                is_lti: process.env.REACT_APP_AUTH === 'LTI',
            };
            setForm((prev) => ({ ...prev, ...incomingCourseData }));
            setIsDeletable(true);
            setLoading(false);
        } else {
            setForm({ ...defaultForm, course_id: uuid() });
            setLoading(false);
        }
    }, [auth.course_id, course, history, location.state]);

    return (
        <PageContext.Provider
            value={{
                loading,
                form,
                isDeletable,
                dialog,
                setDialog,
                errors,
                handleChange,
                openDeleteDialog,
                handleClose,
                submitData,
                setErrors,
                setForm,
                LIST_OF_SCHOOLS,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
